import { useAuthStore } from '@/stores/auth.store';
import { usePermissionStore } from '@/stores/permission.store';

/**
 * Re-reads the signed-in user's permissions once per app boot.
 *
 * Context: a session survives for weeks on silent token refresh without `login()` running
 * again, and no other path re-reads permissions on a page load — only login, the unified-auth
 * handoff, registration, and the 403 recovery do. So a role granted mid-session stayed
 * invisible until the user signed out and back in, with a page refresh (the obvious thing to
 * try) changing nothing. The Pusher role event covers the live case, but only for a session
 * connected at the moment of the grant.
 *
 * Never awaited by the caller and never throws: a failed refresh leaves the cached permissions
 * in place rather than blocking start-up. Callers fire it and move on. A caller that must not
 * act on the cached roles waits for `permissionStore.areRolesRefreshed` instead.
 */
export const refreshPermissionsOnBoot = async (): Promise<void> => {
  const authStore = useAuthStore();

  // The store's tokens are populated later, by the router guard, so reading `isLoggedIn` here
  // would race it. The persisted user alone is not a session either: an expired refresh token
  // clears both tokens but leaves `user` behind, so the fetch would go out unauthenticated and
  // 401. A stored refresh token is what the request can actually be authorised with.
  if (authStore.loggedInUser?.id && localStorage.getItem('refreshToken')) {
    try {
      await authStore.getUserPermissions();
    } catch (error) {
      console.error('Failed to refresh permissions on start-up:', error);
    }
  }

  // A successful refresh has marked the roles current already (`getUserPermissions`). This settles
  // the other two: a failure leaves the stored roles standing, as before, and with no session there
  // is nothing to refresh, since a later login fetches permissions itself.
  usePermissionStore().setRolesRefreshed();
};
